import { create } from 'zustand';
import { PageLocation, PageStatus, Store } from '@/types/store';
import { useDefaultStore } from './defaultStore';

interface StoreState {
    defaultStoreId: number | null;
    stores: Record<number, Store>;
    isLoading: boolean;

    // Actions
    setDefaultStoreId: (defaultStoreId: number) => void;
    addStore: (store: Store) => void;
    setStoreLoading: (loading: boolean) => void;
    clearStore: () => void;

    getFunnels: () => Store[];
}

export const useStoreStore = create<StoreState>()((set, get) => ({
    // Initial state
    defaultStoreId: null,
    stores: {},
    isLoading: false,

    // Actions
    setDefaultStoreId: (defaultStoreId: number) => set({ defaultStoreId }),

    setStoreLoading: (isLoading: boolean) => set({ isLoading }),

    addStore: (store: Store) => {
        // Update stores map
        const storesMap = get().stores;
        storesMap[store.store_id] = store;

        // Update the store store
        set({ stores: storesMap });

        if (store.store_id === get().defaultStoreId) {
            const storeName = store.data?.name;
            const pages = (store.pages || []).map(p => ({ ...p, storeName }));
            const sections = store.sections || [];

            // Filter pages by location
            const nonLandingPages = pages.filter(p => p.data.location !== PageLocation.LANDING);
            const landingPages = pages.filter(p => p.data.location === PageLocation.LANDING);

            // Combine and sort pages with sections by order
            const pagesWithSections = [...nonLandingPages, ...sections].sort((a, b) => a.order - b.order);
            useDefaultStore.getState().setDefaultStore(store, store.store_id, store.type, storeName || '', {
                theme: store.data?.theme || 'default',
                primaryColor: store.data?.primary_color || null,
                secondaryColor: store.data?.secondary_color || null,
                primaryFont: store.data?.primary_font || null,
                sections: sections,
                allPages: pages,
                pages: nonLandingPages,
                landingPages: landingPages,
                publishedPages: pages.filter(p => p.status === PageStatus.PUBLISHED),
                pagesWithSections: pagesWithSections,
            });
        }
        // Update the default store slice with derived data
    },

    clearStore: () => {
        set({
            defaultStoreId: null,
            stores: {},
            isLoading: false,
        });
        // Also clear the default store slice
        useDefaultStore.getState().clearDefaultStore();
    },

    getFunnels: () => {
        const stores = get().stores;
        return Object.values(stores).filter(s => s.type === 'funnel');
    },
}));
